import CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import { isPreviewItemId } from 'owa-quick-compose-controller';
import { surfaceStore } from 'owa-calendar-surface-surface-store';
import {
    CalendarSurfaceItemsConfig,
    overflowItemTemplate,
    isOverflowIndicatorId,
    createItemsDecorator,
    createDragHelperDecorator,
    createMonthItemsDecorator,
} from 'owa-calendar-surface-dategrid';
import { isFeatureEnabled } from 'owa-feature-flags';
import { renderAsAllDay } from 'owa-calendar-types';
import { createMonthDayCellsDecorator, isDragItemId } from 'owa-calendar-surface';
import {
    owaCalendarItemsEventHandlers,
    monthDayCellsEventHandlers,
    monthOverflowItemsHandlers,
    weekOverflowItemsHandlers,
} from './OwaCalendarSurfaceEventHandlers';
import {
    inDayItemTemplate,
    allDayItemTemplate,
    monthItemTemplate,
    blankItemTemplate,
    overflowMonthItemTemplate,
} from './OwaTemplates';

const gridItemsDecorator = createItemsDecorator(surfaceStore, () =>
    isFeatureEnabled('cal-surface-itemExpand')
);
const overflowItemsDecorator = createItemsDecorator(surfaceStore, () => false);
const monthItemsDecorator = createMonthItemsDecorator(surfaceStore);
const dragHelperDecorator = createDragHelperDecorator(surfaceStore);

/**
 * Gets item configuration for given calendar event
 */
export function getItemConfig(model: CalendarEvent): CalendarSurfaceItemsConfig {
    if (isOverflowIndicatorId(model.ItemId.Id)) {
        return weekOverflowItemsConfig;
    }
    if (isPreviewItemId(model.ItemId.Id)) {
        return quickAddPreviewItemConfig;
    }
    if (isDragItemId(model.ItemId.Id)) {
        return renderAsAllDay(model) ? dragDropAllDayItemConfig : dragDropInDayItemConfig;
    }
    return renderAsAllDay(model) ? allDayItemConfig : inDayItemConfig;
}

export function getMonthItemConfig(model: CalendarEvent): CalendarSurfaceItemsConfig {
    if (isOverflowIndicatorId(model.ItemId.Id)) {
        return monthOverflowItemsConfig;
    }
    if (isDragItemId(model.ItemId.Id)) {
        return dragDropMonthItemConfig;
    }
    return monthItemsConfig;
}

/**
 * Configuration for in day calendar events
 */
const inDayItemConfig: CalendarSurfaceItemsConfig = {
    itemsEventHandlers: owaCalendarItemsEventHandlers,
    itemsDecorator: gridItemsDecorator,
    template: inDayItemTemplate,
};

/**
 * Configuration for all day calendar events
 */
const allDayItemConfig: CalendarSurfaceItemsConfig = {
    itemsEventHandlers: owaCalendarItemsEventHandlers,
    itemsDecorator: gridItemsDecorator,
    template: allDayItemTemplate,
};

/**
 * Configuration for quick add preview item
 */
export const quickAddPreviewItemConfig: CalendarSurfaceItemsConfig = {
    itemsDecorator: gridItemsDecorator,
    template: blankItemTemplate,
};

/**
 * Configuration for drag and drop in day item
 */
export const dragDropInDayItemConfig = {
    itemsDecorator: dragHelperDecorator,
    template: inDayItemTemplate,
};

/**
 * Configuration for drag and drop all day item
 */
export const dragDropAllDayItemConfig = {
    itemsDecorator: dragHelperDecorator,
    template: allDayItemTemplate,
};

/**
 * Configuration for month items
 */
export const monthItemsConfig: CalendarSurfaceItemsConfig = {
    itemsEventHandlers: owaCalendarItemsEventHandlers,
    itemsDecorator: monthItemsDecorator,
    template: monthItemTemplate,
};

/**
 * Configuration for overflow items in month view
 */
const monthOverflowItemsConfig: CalendarSurfaceItemsConfig = {
    itemsEventHandlers: monthOverflowItemsHandlers,
    itemsDecorator: overflowItemsDecorator,
    template: overflowMonthItemTemplate,
};

/**
 * Configuration for overflow items in week view
 */
const weekOverflowItemsConfig: CalendarSurfaceItemsConfig = {
    itemsEventHandlers: weekOverflowItemsHandlers,
    itemsDecorator: overflowItemsDecorator,
    template: overflowItemTemplate,
};

/**
 * Configuration for drag and drop items in month view
 */
export const dragDropMonthItemConfig: CalendarSurfaceItemsConfig = {
    itemsDecorator: dragHelperDecorator,
    template: monthItemTemplate,
};

/**
 * Configuration for month day cells
 */
export const monthDayCellConfig = {
    itemsEventHandlers: monthDayCellsEventHandlers,
    itemsDecorator: createMonthDayCellsDecorator(surfaceStore),
};
